import styled, { css, keyframes } from 'styled-components'

export const FieldWrapper = styled.div`
    position: relative;
    width: 100%;
`

export const TextArea = styled.textarea<{ hasError: boolean; variant: 'desktop' | 'mobile'; isRotating: boolean }>(
    ({ hasError, isRotating, variant, theme }) => css`
        box-sizing: border-box;
        width: 100%;
        height: ${variant === 'desktop' ? '6.5rem' : '5.5rem'};
        padding: ${theme.spacing.small} ${theme.spacing.medium};
        border-radius: ${theme.typography.border.radius.xLarge};
        border-style: solid;
        resize: none;
        outline: none;
        background-color: ${theme.colors.white};
        color: ${theme.colors.tfwBlack87};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.375rem;

        ${hasError
            ? css`
                  border-width: ${theme.typography.border.width.xxThin};
                  border-color: ${theme.colors.tfwNegativeMedium};
              `
            : css`
                  border-width: ${variant === 'desktop' ? theme.typography.border.width.xxxThin : '1.5px'};
                  border-color: ${variant === 'desktop' ? theme.colors.tfwDefaultInputBorder : theme.colors.tfwBlack87};
              `}

        ${variant === 'desktop' &&
        !hasError &&
        css`
            box-shadow:
                0 4px 2.5px rgba(0, 0, 0, 0.06),
                0 1px 5px rgba(0, 0, 0, 0.04),
                0 2px 2px rgba(0, 0, 0, 0.08);
        `}

        /* Hidden only while the overlay is drawing it; once the rotation stops the native
           placeholder takes over so the field is never left without a hint. */
        &::placeholder {
            color: ${isRotating ? 'transparent' : theme.colors.tfwBlack38};
        }

        &:focus-visible {
            border-color: ${hasError ? theme.colors.tfwNegativeMedium : theme.colors.tfwViolet};
        }
    `,
)

export const Ghost = styled.span<{ isVisible: boolean; variant: 'desktop' | 'mobile' }>(
    ({ isVisible, variant, theme }) => css`
        position: absolute;
        top: ${theme.spacing.small};
        left: ${theme.spacing.medium};
        right: ${theme.spacing.medium};
        color: ${theme.colors.tfwBlack38};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.375rem;
        pointer-events: none;
        overflow: hidden;
        opacity: ${isVisible ? 1 : 0};
        transition: opacity 300ms ease;

        /* Sits on the textarea's own text baseline, whose border width differs per variant. */
        margin-top: ${variant === 'desktop' ? '1px' : '1.5px'};

        @media (prefers-reduced-motion: reduce) {
            transition: none;
        }
    `,
)

const caretBlink = keyframes`
    0%, 49% {
        opacity: 1;
    }
    50%, 100% {
        opacity: 0;
    }
`

export const Caret = styled.span(
    ({ theme }) => css`
        display: inline-block;
        width: 1px;
        height: 0.9375rem;
        margin-left: 1px;
        vertical-align: text-bottom;
        background-color: ${theme.colors.tfwViolet};
        animation: ${caretBlink} 1.06s step-end infinite;

        @media (prefers-reduced-motion: reduce) {
            display: none;
        }
    `,
)

export const ErrorMessage = styled.p(
    ({ theme }) => css`
        margin: ${theme.spacing.xxSmall} 0 0;
        color: ${theme.colors.tfwNegativeMedium};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.375rem;
    `,
)
